import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode, useRef } from 'react';
import { UserContextType, useAuth } from '../auth';
import Connector from './signalr-connection';

export interface ChatMessage {
    user: string;
    text: string;
    id: string;
    isOwn: boolean;
}

interface ChatContextType {
    messages: ChatMessage[];
    onlineUsers: string[];
    unreadCount: number;
    hasNewUser: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    sendMessage: (text: string) => void;
    clearNotifications: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

interface ChatProviderProps {
    children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
    const { getUserName, getToken } = useContext<UserContextType>(useAuth);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [connector, setConnector] = useState<{ events: any, newMessage: any } | null>(null);
    const [isOpen, setIsOpenState] = useState(false);
    const isOpenRef = useRef(false);

    // Sync ref with state
    const setIsOpen = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
        setIsOpenState(prev => {
            const newState = typeof val === 'function' ? val(prev) : val;
            isOpenRef.current = newState;
            return newState;
        });
    }, []);

    // Also sync on effect in case set via other means (though we wrap setter)
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    // Notification state
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasNewUser, setHasNewUser] = useState(false);

    const myName = getUserName ? getUserName() : "Anonymous";

    // Initialize connection
    useEffect(() => {
        if (!getToken) return;

        const token = getToken();
        if (token) {
            const conn = Connector(token);
            setConnector(conn);

            conn.events(
                (username: string, message: string) => {
                    const newMsg: ChatMessage = {
                        id: Date.now().toString() + Math.random().toString(),
                        user: username,
                        text: message,
                        isOwn: false
                    };

                    if (username === myName || username === (getUserName ? getUserName() : "")) {
                        newMsg.isOwn = true;
                    }

                    setMessages(prev => [...prev, newMsg]);

                    // Handle notifications using ref
                    if (!isOpenRef.current && !newMsg.isOwn) {
                        setUnreadCount(prev => prev + 1);
                    }
                },
                (users: string[]) => {
                    setOnlineUsers(prev => {
                        // Check for new users (simple check: count increased)
                        if (users.length > prev.length) {
                            if (!isOpenRef.current) {
                                setHasNewUser(true);
                            }
                        }
                        return users;
                    });
                },
                (history: any[]) => {
                    const historyMsgs: ChatMessage[] = history.map((h: any) => ({
                        id: Date.now().toString() + Math.random().toString(),
                        user: h.user,
                        text: h.message,
                        isOwn: h.user === myName // Basic check for history
                    }));
                    setMessages(prev => [...historyMsgs, ...prev]);
                }
            );
        }
    }, [getToken, myName]);

    // Clear notifications when chat is opened
    useEffect(() => {
        if (isOpen) {
            setUnreadCount(0);
            setHasNewUser(false);
        }
    }, [isOpen]);

    const sendMessage = useCallback((text: string) => {
        if (!text.trim() || !connector) return;
        connector.newMessage(myName, text);
    }, [connector, myName]);

    const clearNotifications = useCallback(() => {
        setUnreadCount(0);
        setHasNewUser(false);
    }, []);

    const value = {
        messages,
        onlineUsers,
        unreadCount,
        hasNewUser,
        isOpen,
        setIsOpen, // This is now our wrapped setter
        sendMessage,
        clearNotifications
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};
